import { useEffect, useRef, useState } from "react";
import { Settings } from "lucide-react";
import { cn } from "../lib/utils.ts";
import type { Activity } from "./ActivitySelector.tsx";

export interface ModelOption {
  id: string;
  label: string;
  hint: string;
}

export const CHAT_MODELS: ModelOption[] = [
  { id: "gpt-4o-mini", label: "GPT-4o mini", hint: "Fast & cheap" },
  { id: "gpt-4o", label: "GPT-4o", hint: "Higher quality" },
  { id: "gpt-4.1-mini", label: "GPT-4.1 mini", hint: "Balanced" },
  { id: "gpt-4.1", label: "GPT-4.1", hint: "Best quality" },
];

export interface VoiceOption {
  id: string;
  label: string;
}

export const VOICES: VoiceOption[] = [
  { id: "alloy", label: "Alloy" },
  { id: "ash", label: "Ash" },
  { id: "ballad", label: "Ballad" },
  { id: "coral", label: "Coral" },
  { id: "echo", label: "Echo" },
  { id: "fable", label: "Fable" },
  { id: "nova", label: "Nova" },
  { id: "onyx", label: "Onyx" },
  { id: "sage", label: "Sage" },
  { id: "shimmer", label: "Shimmer" },
  { id: "verse", label: "Verse" },
];

export const INSTRUCTION_PRESETS: { label: string; value: string }[] = [
  { label: "Cheerful", value: "Speak in a cheerful and positive tone." },
  { label: "Calm", value: "Speak calmly and gently, with a soothing tone." },
  { label: "Excited", value: "Speak with high energy and excitement." },
  { label: "Whisper", value: "Speak softly, like a whisper." },
  {
    label: "Slow & clear",
    value: "Speak slowly and clearly, enunciating each word.",
  },
];

export type MemoryProvider = "openai" | "zeroclaw" | "zeroclaw-session";

export const MEMORY_PROVIDERS: { id: MemoryProvider; label: string }[] = [
  { id: "openai", label: "OpenAI" },
  { id: "zeroclaw", label: "ZeroClaw" },
  { id: "zeroclaw-session", label: "ZeroClaw Session" },
];

export interface SettingsValue {
  voice: string;
  ttsInstructions: string;
  ttsSpeed: number;
  memoryProvider: MemoryProvider;
  chatModel: string;
}

export const DEFAULT_SETTINGS: SettingsValue = {
  voice: "fable",
  ttsInstructions: "",
  ttsSpeed: 1.25,
  memoryProvider: "zeroclaw-session",
  chatModel: CHAT_MODELS[0].id,
};

interface Props {
  value: SettingsValue;
  activity: Activity;
  onChange: (v: SettingsValue) => void;
}

export function SettingsPanel({ value, activity, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Settings"
        aria-expanded={open}
        className={cn(
          "h-9 w-9 grid place-items-center rounded-full transition-colors",
          open
            ? "bg-white text-canvas"
            : "bg-white/10 text-white hover:bg-white/20",
        )}
      >
        <Settings className="h-4 w-4" strokeWidth={2.5} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Settings"
          className="absolute right-0 top-12 z-20 w-80 max-h-[80vh] overflow-y-auto rounded-2xl bg-canvas/95 backdrop-blur-md text-white shadow-2xl border border-white/15 p-4"
        >
          <h3 className="text-xs uppercase tracking-widest opacity-60 mb-3">
            Output
          </h3>

          <label className="block text-sm font-semibold mb-2">Voice</label>
          <select
            value={value.voice}
            onChange={(e) => onChange({ ...value, voice: e.target.value })}
            className="w-full bg-white/10 hover:bg-white/15 text-white rounded-xl px-3 py-2 mb-4 outline-none focus:ring-2 focus:ring-white/40"
          >
            {VOICES.map((v) => (
              <option key={v.id} value={v.id} className="text-canvas">
                {v.label}
              </option>
            ))}
          </select>

          <label className="block text-sm font-semibold mb-2">
            Speaking style
          </label>
          <div className="flex flex-wrap gap-1 mb-2">
            {INSTRUCTION_PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() =>
                  onChange({ ...value, ttsInstructions: p.value })}
                className="text-xs px-2 py-1 rounded-full bg-white/10 hover:bg-white/20"
              >
                {p.label}
              </button>
            ))}
            {value.ttsInstructions && (
              <button
                onClick={() => onChange({ ...value, ttsInstructions: "" })}
                className="text-xs px-2 py-1 rounded-full bg-white/5 hover:bg-white/15 opacity-70"
              >
                Reset
              </button>
            )}
          </div>
          <textarea
            value={value.ttsInstructions}
            onChange={(e) =>
              onChange({ ...value, ttsInstructions: e.target.value })}
            placeholder="e.g. Speak warmly, with a slight smile."
            rows={2}
            className="w-full bg-white/10 hover:bg-white/15 text-white placeholder:text-white/40 rounded-xl px-3 py-2 mb-4 outline-none focus:ring-2 focus:ring-white/40 resize-none"
          />

          <label className="flex items-center justify-between text-sm font-semibold mb-2">
            <span>Speed</span>
            <span className="text-xs opacity-70 font-normal">
              {value.ttsSpeed.toFixed(2)}×
            </span>
          </label>
          <input
            type="range"
            min={0.5}
            max={2}
            step={0.05}
            value={value.ttsSpeed}
            onChange={(e) =>
              onChange({ ...value, ttsSpeed: Number(e.target.value) })}
            className="w-full mb-2 accent-white"
          />

          {activity === "memory" && (
            <>
              <hr className="my-4 border-white/10" />
              <h3 className="text-xs uppercase tracking-widest opacity-60 mb-3">
                Memory
              </h3>
              <label className="block text-sm font-semibold mb-2">
                Provider
              </label>
              <div
                role="radiogroup"
                aria-label="Memory provider"
                className="inline-flex bg-white/10 rounded-full p-1 gap-1 mb-4"
              >
                {MEMORY_PROVIDERS.map((p) => {
                  const active = p.id === value.memoryProvider;
                  return (
                    <button
                      key={p.id}
                      role="radio"
                      aria-checked={active}
                      onClick={() =>
                        onChange({ ...value, memoryProvider: p.id })}
                      className={cn(
                        "px-3 py-1 rounded-full text-xs font-semibold transition-colors",
                        active
                          ? "bg-white text-canvas"
                          : "text-white/80 hover:text-white hover:bg-white/10",
                      )}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
              {value.memoryProvider === "openai" && (
                <>
              <label className="block text-sm font-semibold mb-2">
                AI model
              </label>
              <div className="flex flex-col gap-1">
                {CHAT_MODELS.map((m) => {
                  const active = m.id === value.chatModel;
                  return (
                    <button
                      key={m.id}
                      onClick={() =>
                        onChange({ ...value, chatModel: m.id })}
                      aria-pressed={active}
                      className={cn(
                        "flex items-center justify-between gap-3 px-3 py-2 rounded-xl text-left transition-colors",
                        active
                          ? "bg-white text-canvas"
                          : "bg-white/5 hover:bg-white/15",
                      )}
                    >
                      <span className="font-medium">{m.label}</span>
                      <span
                        className={cn(
                          "text-xs",
                          active ? "opacity-70" : "opacity-50",
                        )}
                      >
                        {m.hint}
                      </span>
                    </button>
                  );
                })}
              </div>
                </>
              )}
              {value.memoryProvider === "zeroclaw" && (
                <p className="text-xs opacity-60 leading-relaxed">
                  One-shot HTTP webhook to the ZeroClaw agent configured in
                  {" "}<code>config.yaml</code>.
                </p>
              )}
              {value.memoryProvider === "zeroclaw-session" && (
                <p className="text-xs opacity-60 leading-relaxed">
                  Persistent WebSocket session to <code>/ws/chat</code>. ZeroClaw
                  can push messages on its own (e.g. cron jobs); they're
                  translated and spoken automatically.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
